if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      formPopoverSubmitted = false;

      get sectionId() {
        return this.getAttribute(this.selectors.sectionId);
      }

      get formPopover() {
        return this.querySelector(`form-popover[data-section-id="${this.sectionId}"]`);
      }

      get form() {
        return this.querySelector('form');
      }

      constructor() {
        super();

        this.addEventListener('product-form-popover:submitted', ({ target }) => {
          if (!this.contains(target)) return;
          this.formPopoverSubmitted = true;
        });

        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');

            if (this.formPopoverSubmitted) return;

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

if (!customElements.get('form-popover')) {
  customElements.define(
    'form-popover',
    class FormPopover extends HTMLElement {
      guestFields = undefined;
      cartUpdateUnsubscriber = undefined;
      izyrentCalender = undefined;

      // Define a key for Local Storage
      LOCAL_STORAGE_KEY = 'roomBookingFormDraft';

      selectors = {
        sectionId: 'data-section-id',
        formId: 'data-form-id',
        bookNow: 'button[data-book-now]',
        submit: '.js-button-submit',
        close: '.js-popup-close',
        fields: '.js-popup-field',
        popupFields: '[data-popup-field]', // Used to target parent wrappers
        guests: '#ProductForm',
        guestsValue: '2 Guests',
        warningClass: 'field-warning',
        addToCart: 'button[type="submit"]',
        izyrent: 'izyrent',
        customerLogin: 'data-customer-login',
      }

      constructor() {
        super();
        this.clickEvent = this.handleClickEvent.bind(this);
        this.validate = this.handleFormValidation.bind(this);
        this.toggleAdultInputs = this.handleToggleAdultInputs.bind(this);

        this.izyrentReady = this.handleIzyrentReady.bind(this);
      }

      connectedCallback() {
        this.guests?.addEventListener('change', this.toggleAdultInputs);

        this.addEventListener('click', this.clickEvent);
        this.addEventListener('change', this.validate);

        // --- CACHING INTEGRATION: Add event listeners for saving ---
        this.addEventListener('input', this.saveDraft.bind(this));
        this.addEventListener('change', this.saveDraft.bind(this));

        // --- CACHING INTEGRATION: Load the draft data when connected ---
        this.loadDraft();

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'cart-items') {
            return;
          }
          return this.onCartUpdate();
        });

        if (this.productForm)
          this.observeSingleVariantQuickAdd(this.productForm);
        document.addEventListener('izyrentGlobalReady', this.izyrentReady);

        // console.log(this.popupFields);

        if (this.loginButton) {
          this.bindElements();
          this.unsubscribe = subscribe('dateOptionsUpdated', this.bindElements.bind(this));
        } else {
          let ckDocReady = function ckDocReady(fn) {
            // see if DOM is already available
            if (document.readyState === "loading") {
              document.addEventListener("DOMContentLoaded", fn);
            } else {
              setTimeout(fn, 1);
            }
          };

          const booknowButton = this.querySelector(this.selectors.bookNow);

          let openBookPopup = function openBookPopup() {
            setTimeout(() => {
              booknowButton?.click();
            }, 5000);
          }

          ckDocReady(openBookPopup);
        }
      }

      disconnectedCallback() {
        this.guests?.removeEventListener('change', this.toggleAdultInputs);

        this.removeEventListener('click', this.clickEvent);
        this.removeEventListener('change', this.validate);

        document.removeEventListener('izyrentGlobalReady', this.izyrentReady);

        // --- CACHING INTEGRATION: Remove event listeners for saving ---
        this.removeEventListener('input', this.saveDraft.bind(this));
        this.removeEventListener('change', this.saveDraft.bind(this));

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.unsubscribe) {
          this.unsubscribe();
        }
      }

      // New method to set up the MutationObserver
      observeSingleVariantQuickAdd(targetElement) {
        // Options for the observer (what to observe)
        const observerOptions = {
          childList: true, // Observe direct children additions/removals
          attributes: true, // Observe attribute changes
          subtree: true, // Observe all descendants (subtree)
          characterData: true, // Observe text content changes
        };

        // Callback function to execute when mutations are observed
        const callback = (mutationsList, observer) => {
          for (const mutation of mutationsList) {
            if (mutation.target.getAttribute('id') == this.selectors.izyrent) {
              // console.log('Mutation Type:', mutation.type);
              // console.log('Target Node:', mutation.target);
              // console.log('Target ID:', mutation.target.getAttribute('id'));
              // console.log('Mutation details:', mutation);

              this.izyrentReady();
              // You can add more specific logging or logic here
            }
          }
        };

        // Create an observer instance linked to the callback function
        const observer = new MutationObserver(callback);

        // Start observing the target node for configured mutations
        observer.observe(targetElement, observerOptions);

        // OPTIONAL: Store the observer if you need to disconnect it later,
        // e.g., in disconnectedCallback or when the relevant operation completes.
        this._mutationObserver = observer;
      }

      handleIzyrentReady(event) {
        this.izyrentCalender = document.querySelector(`#${this.selectors.izyrent}`);
        if (!this.izyrentCalender) return;
        const izyCalendarStart = this.izyrentCalender.querySelector('#startDate');
        const izyCalendarEnd = this.izyrentCalender.querySelector('#endDate');

        this.izyrentCalender.addEventListener('change', (event) => {
          if (!!izyCalendarStart.value && !!izyCalendarEnd.value) {
            if (this.querySelector(this.selectors.bookNow)?.hasAttribute('disabled')) {
              this.querySelector(this.selectors.bookNow).removeAttribute('disabled');
            }

            // --- NEW CODE TO UPDATE URL WITH DATES ---
            const datePrams = izyCalendarStart.value.replaceAll(' to ', '-');
            this.updateUrl(datePrams);

            if (this.loginButton) {
              publish('dateOptionsUpdated', { connectedParent: this, event: event, target: event.target });
            }
          } else {
            if (!this.querySelector(this.selectors.bookNow)?.hasAttribute('disabled')) {
              this.querySelector(this.selectors.bookNow)?.setAttribute('disabled', true);
            }
          }
        });
      }

      /**
       * Updates the browser URL with the new start and end dates.
       * @param {string} datePrams - The selected dates (e.g., '2025-11-26').
       */
      updateUrl(datePrams) {
        if (!window.history.pushState) return;

        const currentUrl = new URL(window.location.href);
        const newDatesParam = `${datePrams}`;

        // Check if the existing URL has a 'dates' parameter
        if (currentUrl.searchParams.has('dates')) {
          currentUrl.searchParams.set('dates', newDatesParam);
        } else {
          // If no 'dates' parameter, append it to the path
          const pathWithoutDates = currentUrl.pathname.replace(/\/dates=[^/?]+/, ''); // Clean up old path format if it existed

          // Construct the new path: /product-handle?dates=start-end or /product-handle/dates=start-end
          // Based on your example URL structure (using a query parameter: ?dates=...), we'll use searchParams.
          currentUrl.searchParams.set('dates', newDatesParam);
        }

        // Update the browser's history without reloading the page
        window.history.pushState({ path: currentUrl.href }, '', currentUrl.href);
      }

      bindElements() {
        const shopUrl = window.shopUrl;
        const currentPath = document.URL;
        const redirectUrl = `${this.loginButton.dataset.accountPage}?checkout_url=${currentPath.split(shopUrl)[1]}`

        this.loginButton.setAttribute('href', `${redirectUrl}`);
      }

      // -------------------------------------------------------------------
      // ------------------- NEW CACHING METHODS (REVISED) -----------------
      // -------------------------------------------------------------------

      /**
       * Saves the data from all form fields (including select/input/textarea) 
       * to Local Storage as a JSON string by iterating over this.popupFields.
       */
      saveDraft() {
        if (!window.localStorage) return;

        let draft = {};

        // Iterate over the parent field elements
        this.popupFields.forEach(fieldWrapper => {
          // Find the actual input element within the wrapper (input, select, or textarea)
          const element = fieldWrapper.querySelector('input, select, textarea');

          if (element && element.name) {
            if (element.type === 'checkbox') {
              draft[element.name] = element.checked;
            } else if (element.type === 'radio') {
              // Only save the value of the checked radio button
              if (element.checked) {
                draft[element.name] = element.value;
              }
            } else {
              // Covers text, select, textarea, date, etc.
              draft[element.name] = element.value;
            }
          }
        });

        // Save the JSON string
        localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(draft));
      }

      /**
       * Loads data from Local Storage and fills the form fields using the element name.
       */
      loadDraft() {
        if (!window.localStorage) return;

        const savedData = localStorage.getItem(this.LOCAL_STORAGE_KEY);

        if (!savedData) return;

        const draft = JSON.parse(savedData);

        // Apply saved data to all form elements
        for (const [key, value] of Object.entries(draft)) {
          // Find the actual element by its 'name' attribute
          const element = this.querySelector(`[name="${key}"]`);

          if (element) {
            if (element.type === 'checkbox') {
              element.checked = value;
            } else if (element.type === 'radio') {
              // For radio buttons, we need to find the specific element with the matching value
              const radioElement = this.querySelector(`input[name="${key}"][value="${value}"]`);
              if (radioElement) {
                radioElement.checked = true;
              }
            } else {
              // Handle text inputs, select dropdowns, textareas
              element.value = value;
            }
          }
        }
      }

      /**
       * Clears the draft from Local Storage after successful submission.
       */
      clearDraft() {
        if (window.localStorage) {
          localStorage.removeItem(this.LOCAL_STORAGE_KEY);
          console.log('Form data draft cleared from Local Storage.');
        }
      }

      // -------------------------------------------------------------------
      // ----------------- END NEW CACHING METHODS (REVISED) ---------------
      // -------------------------------------------------------------------

      handleToggleAdultInputs(event) {
        this.guestFields = this.fields.filter(item => item.querySelector(`[data-guests="${this.selectors.guestsValue}"]`));
        const matchGuestFields = this.fields.filter(item => item.querySelector(`[data-guests="${this.guests.value}"]`));
        if (matchGuestFields.length == this.guestFields.length) {
          this.guestFields.forEach(field => {
            const fieldInput = field.querySelector('select') || field.querySelector('input');
            fieldInput.hasAttribute('disabled') && fieldInput.removeAttribute('disabled');
          })
        } else {
          this.guestFields.forEach(field => {
            const fieldInput = field.querySelector('select') || field.querySelector('input');
            !fieldInput.hasAttribute('disabled') && fieldInput.setAttribute('disabled', 'disabled');
          })
        }
      }

      handleClickEvent(event) {
        if (!this.submitButton?.contains(event.target)) return;
        this.validateAllFields();

        if (this.fieldsValidated)
          this.submitForm();
      }

      handleFormValidation(event) {
        const fieldInput = event.target;
        const field = event.target.closest(this.selectors.fields);
        this.validateField(field, fieldInput);
      }

      validateAllFields() {
        this.activeFields.forEach(field => {
          const fieldInput = field.querySelector('select') || field.querySelector('input');
          this.validateField(field, fieldInput);
        });
      }

      validateField(field, fieldInput) {
        if (fieldInput.tagName === 'SELECT' && !fieldInput.value) {
          this.showWarning(field, this.selectors.warningClass);
        } else {
          this.removeWarning(field, this.selectors.warningClass);
        }

        if (fieldInput.tagName === 'INPUT') {
          if (!fieldInput.value) {
            this.showWarning(field, this.selectors.warningClass);
          } else {
            if (fieldInput.value.length >= fieldInput.minLength) {
              this.removeWarning(field, this.selectors.warningClass);
            } else {
              this.showWarning(field, this.selectors.warningClass);
            }
          }
        }
      }

      showWarning(el, warningClass) {
        !el?.classList.contains(warningClass) && el?.classList.add(warningClass);
      }

      removeWarning(el, warningClass) {
        el?.classList.contains(warningClass) && el?.classList.remove(warningClass);
      }

      submitForm() {
        this.form?.querySelector(this.selectors.addToCart)?.click();
        // this.form?.querySelector(this.selectors.addToCart)?.click(), this.closeButton?.click();

        if (!this.form.checkValidity()) return;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.submitButton.querySelector('.loading__spinner').classList.remove('hidden');

        this.dispatchEvent(new CustomEvent('product-form-popover:submitted', { bubbles: true }));
      }

      onCartUpdate() {
        // --- CACHING INTEGRATION: Clear draft when cart update (submission) is complete ---
        this.clearDraft();

        window.location.href = '/checkout';
      }

      get loginButton() {
        return this.querySelector(`[${this.selectors.customerLogin}]`);
      }

      get closeButton() {
        return this.querySelector(this.selectors.close);
      }

      get submitButton() {
        return this.querySelector(this.selectors.submit);
      }

      get formId() {
        return this.getAttribute(this.selectors.formId)
      }

      get sectionId() {
        return this.getAttribute(this.selectors.sectionId);
      }

      get productForm() {
        return document.querySelector(`product-form[${this.selectors.sectionId}="${this.sectionId}"]`);
      }

      get form() {
        return document.querySelector(`product-form[${this.selectors.sectionId}="${this.sectionId}"] form`);
      }

      get fields() {
        return Array.from(this.querySelectorAll(this.selectors.fields));
      }

      get popupFields() {
        return Array.from(this.querySelectorAll(this.selectors.popupFields));
      }

      get activeFields() {
        return this.fields?.filter(item => item.querySelector('select:not([disabled])') || item.querySelector('input:not([disabled])'));
      }

      get fieldsValidated() {
        return Boolean(!this.activeFields.filter(item => item.classList.contains(this.selectors.warningClass)).length);
      };

      get guests() {
        return document.querySelector(`${this.selectors.guests}-${this.formId}`);
      }
    }
  )
}

if (!customElements.get('lodgify-booking')) {
  customElements.define(
    'lodgify-booking',
    class LodgifyBooking extends HTMLElement {
      flatpicker = null;
      flatpickerOptions = {};
      sectionId = this.dataset.sectionId;
      serverTimeStr = this.dataset.serverTime;

      get flatpickerEl() {
        return this.querySelector(`[id^="flatpickr-${this.sectionId}"]`);
      }

      get booknowButton() {
        return this.querySelector('a[data-book-now]');
      }

      constructor() {
        super();
      }

      connectedCallback() {
        this.setupBooking();
      }

      async setupBooking() {
        // const blocked = await this.fetchBlockedDates();
        // console.log(blocked);

        // this.initFlatpicker(blocked);
        this.initFlatpicker();
        this.flatpickerEl.addEventListener('change', () => {
          if (this.flatpicker.selectedDates.length < 2) return;
        });

        console.log(this.serverTimeStr);
        console.log(this.dataset.lodgifyPropertyId);
        console.log(this.querySelector('a[data-book-now]'));

        this.fetchBlockedDates();
      }

      async fetchBlockedDates() {
        const options = {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'X-ApiKey': 'UmXUzedVALn8pEtF44DAkomrzwVISSLiOeaxrWAJrTybC+00Da72efGFkV4TRmDy'
          }
        };

        // fetch(`https://api.lodgify.com/v2/availability/${this.dataset.lodgifyPropertyId}?start=2026-01-02T00%3A00%3A00&end=2026-01-10T00%3A00%3A00&includeDetails=true`, options)
        //   .then(res => res.json())
        //   .then(res => console.log(res))
        //   .catch(err => console.log(err));

        try {
          // 1. You MUST 'await' the fetch
          const response = await fetch(`https://api.lodgify.com/v2/availability/${this.dataset.lodgifyPropertyId}?start=2026-01-02T00%3A00%3A00&end=2026-01-10T00%3A00%3A00&includeDetails=true`, options);
          
          console.log("Lodgify response:", response);

          // 2. You MUST 'await' the json parsing
          const data = await response.json();
          
          // 3. Log it here to verify
          console.log("Lodgify Data:", data);

          // 4. Return the data so 'const blocked' in setupBooking actually gets a value
          return data; 

        } catch (err) {
          console.error("Fetch Error:", err);
          return []; // Return empty array on failure so initFlatpicker doesn't crash
        }
      }

      initFlatpicker(blockedDatesArray) {
        const arrivalDate = new Date(this.dataset.serverTime);
        const departureDate = new Date(arrivalDate);
        departureDate.setDate(departureDate.getDate() + 3);

        this.flatpickerOptions = {
          mode: "range",
          dateFormat: "Y-m-d",
          minDate: arrivalDate,
          // disable: blockedDatesArray,
          defaultDate: [arrivalDate, departureDate],
          // animate: true,
          static: true
        }

        this.flatpicker = flatpickr(this.flatpickerEl, this.flatpickerOptions);
      }
    }
  )
}